import { useCallback, useRef } from 'react'
import {
  addEdge,
  type Node,
  type Edge,
  type Connection,
  type EdgeChange,
  type ReactFlowInstance,
  type OnConnectEnd,
} from '@xyflow/react'
import type { NodeCategory } from '@bandos/shared'
import type { ReactFlowNodeData } from '../../../types/mindmap-components'
import { createEdge } from '../../../utils'
import { COLLAPSE_ZOOM_THRESHOLD } from '../utils/zoomUtils'
import { getTargetInfo } from '../Menus/CategoryOptionsList.utils'

type SetEdges = React.Dispatch<React.SetStateAction<Edge[]>>

interface UseEdgeHandlersDeps {
  nodes: Node<ReactFlowNodeData>[]
  edges: Edge[]
  nodeMap: Map<string, Node<ReactFlowNodeData>>
  nodesWithParent: Set<string>
  setEdges: SetEdges
  onEdgesChange: (changes: EdgeChange[]) => void
  reactFlowInstanceRef: React.RefObject<ReactFlowInstance<Node<ReactFlowNodeData>, Edge> | null>
  executeEdgeDropNodeCreation: (
    category: NodeCategory,
    position: { x: number; y: number },
    sourceNodeId: string
  ) => void
  executeParentDropNodeCreation: (
    category: NodeCategory,
    position: { x: number; y: number },
    nodeId: string
  ) => void
}

export function useEdgeHandlers({
  nodes,
  edges,
  nodeMap,
  nodesWithParent,
  setEdges,
  onEdgesChange,
  reactFlowInstanceRef,
  executeEdgeDropNodeCreation,
  executeParentDropNodeCreation,
}: UseEdgeHandlersDeps) {
  const lastEdgeDropPositionRef = useRef<{ x: number; y: number } | null>(null)

  /**
   * Handles React Flow edge changes.
   * Filters out 'remove' and 'select' to prevent edge deletion and selection.
   */
  const handleEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      const filteredChanges = changes.filter(
        (change) => change.type !== 'remove' && change.type !== 'select'
      )
      if (filteredChanges.length > 0) {
        onEdgesChange(filteredChanges)
      }
    },
    [onEdgesChange]
  )

  /**
   * Validates whether a connection is allowed.
   * Prevents connecting to a node that already has a parent (incoming edge).
   * This enforces a tree structure where each node can have at most one parent.
   */
  const isValidConnection = useCallback(
    (connection: Connection) => {
      if (!connection.target) return false

      // Check if target node already has a parent (incoming edge) - O(1) lookup
      return !nodesWithParent.has(connection.target)
    },
    [nodesWithParent]
  )

  /**
   * Handles creating a new edge when user connects two nodes.
   * Creates an edge using the createEdge utility and adds it to the edges state.
   */
  const handleConnect = useCallback(
    (connection: Connection) => {
      if (!connection.source || !connection.target) {
        return
      }

      // Prevent connections when zoomed out to collapsed/mini view
      const currentZoom = reactFlowInstanceRef.current?.getZoom() ?? 1
      if (currentZoom < COLLAPSE_ZOOM_THRESHOLD) return

      const newEdge = createEdge(connection.source, connection.target, nodes, edges, {
        sourceHandle: connection.sourceHandle ?? null,
        targetHandle: connection.targetHandle ?? null,
      })
      setEdges((edges) => addEdge(newEdge, edges))
    },
    [setEdges, nodes, edges, reactFlowInstanceRef]
  )

  /**
   * Handles the end of a connection drag operation.
   * When an edge is dragged and dropped on the empty canvas (invalid connection),
   * this creates a new node at the drop position.
   *
   * For left-side handle drags (parent creation), creates a parent node directly.
   * For right-side handle drags (child creation), creates a child node directly.
   */
  const onConnectEnd: OnConnectEnd = useCallback(
    (event, connectionState) => {
      // Prevent connections when zoomed out to collapsed/mini view
      const currentZoom = reactFlowInstanceRef.current?.getZoom() ?? 1
      if (currentZoom < COLLAPSE_ZOOM_THRESHOLD) return

      // when a connection is dropped on the pane it's not valid
      if (
        (connectionState.isValid === false || connectionState.isValid === null) &&
        connectionState.fromNode
      ) {
        const sourceNode = nodeMap.get(connectionState.fromNode.id)

        // Check if this is a left-side drag (for parent creation)
        const isLeftHandleDrag = connectionState.fromHandle?.id === 'left-source'

        if (isLeftHandleDrag) {
          // Parent creation via left-side drag
          const nodeCategory = sourceNode?.data.category
          if (!nodeCategory) return

          // Determine parent category based on node type
          let parentCategory: NodeCategory | null = null
          if (nodeCategory === 'need') {
            parentCategory = 'venture'
          } else if (nodeCategory === 'idea') {
            parentCategory = 'need'
          }

          if (!parentCategory) return

          const { clientX, clientY } = 'changedTouches' in event ? event.changedTouches[0] : event

          const flowPosition = reactFlowInstanceRef.current?.screenToFlowPosition({
            x: clientX,
            y: clientY,
          })

          if (flowPosition) {
            // Create parent node at drop position
            executeParentDropNodeCreation(parentCategory, flowPosition, connectionState.fromNode.id)
          }
          return
        }

        // Right-side drag - child creation
        // Check if the source node is an information node - information nodes cannot create child nodes
        if (sourceNode?.data.category === 'information') {
          // Information nodes cannot create children - silently ignore
          return
        }

        // Prevent event propagation to avoid conflicts
        event.preventDefault()
        event.stopPropagation()

        const { clientX, clientY } = 'changedTouches' in event ? event.changedTouches[0] : event

        // Convert screen position to flow position for node creation
        const flowPosition = reactFlowInstanceRef.current?.screenToFlowPosition({
          x: clientX,
          y: clientY,
        })

        if (flowPosition) {
          lastEdgeDropPositionRef.current = flowPosition
          // Update global reference
          ;(
            window as Window &
              typeof globalThis & { lastEdgeDropPosition: { x: number; y: number } | null }
          ).lastEdgeDropPosition = flowPosition

          // Determine child category from source node category
          const effectiveCategory = sourceNode?.data.category ?? 'idea'
          const targetInfo = getTargetInfo(effectiveCategory)
          const childCategory = targetInfo?.targetCategory ?? 'idea'

          // Create node directly at drop position
          executeEdgeDropNodeCreation(childCategory, flowPosition, connectionState.fromNode.id)
        }
      }
    },
    [executeEdgeDropNodeCreation, nodeMap, executeParentDropNodeCreation, reactFlowInstanceRef]
  )

  return { handleEdgesChange, isValidConnection, handleConnect, onConnectEnd }
}
